import useStyles from 'isomorphic-style-loader/useStyles';
import { ChevronDown, Download, Plus, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Label, Loader, Popup, Table } from 'semantic-ui-react';

import CancelButton from '@components/cancel-button';
import Modal, { useModal } from '@components/modal';
import raiseToast from '@components/toast';
import Toggle from '@components/toggle';

import {
  useGetMadRobotRulesQuery,
  useGetMadRobotStateQuery,
  usePutMadRobotRulesMutation,
  usePutMadRobotStateMutation,
} from './madrobot-api';
import {
  blankDraft,
  downloadRulesFile,
  stableStringify,
  tempId,
} from './madrobot-modal.utils';
import { MAD_ROBOT_PRESETS } from './madrobot-presets';
import { RuleEditor } from './madrobot-rule-editor';
import { SortableRulesBody } from './madrobot-rules-table';
import type { Rule } from './madrobot.models';
import { rulesPayloadSchema } from './madrobot.schema';
import s from './madrobot-modal.scss';

export const MadRobotModal = () => {
  useStyles(s);
  const { close, isOpen } = useModal('madrobot');

  // `isLoading` (not `isFetching`) so the full-modal loader only shows on the
  // first fetch. Background refetches — e.g. the refetch that arming or saving
  // triggers via tag invalidation — keep the current content on screen instead
  // of collapsing the body to a spinner and resizing the modal.
  const { data: serverState, isLoading: isLoadingState } =
    useGetMadRobotStateQuery(undefined, { skip: !isOpen });
  const { data: serverRules, isLoading: isLoadingRules } =
    useGetMadRobotRulesQuery(undefined, { skip: !isOpen });

  const [putState, { isLoading: isSavingState }] =
    usePutMadRobotStateMutation();
  const [putRules, { isLoading: isSavingRules }] =
    usePutMadRobotRulesMutation();

  const [localRules, setLocalRules] = useState<Rule[]>([]);
  const [draft, setDraft] = useState<Rule | null>(null);
  const [isPresetMenuOpen, setIsPresetMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (serverRules) setLocalRules(serverRules);
  }, [serverRules]);

  const isDirty = useMemo(
    () => stableStringify(localRules) !== stableStringify(serverRules ?? []),
    [localRules, serverRules],
  );

  // Signatures of the server-saved rules so each row can tell whether it's live
  // (red accent) or an unsaved edit/addition (yellow).
  const savedSignatures = useMemo(
    () => new Set((serverRules ?? []).map(stableStringify)),
    [serverRules],
  );

  // Ids the server currently has enabled (injecting when armed). A locally
  // disabled rule still in this set is an unsaved disable — flagged yellow.
  const serverEnabledIds = useMemo(
    () =>
      new Set(
        (serverRules ?? []).filter(rule => rule.enabled).map(rule => rule.id),
      ),
    [serverRules],
  );

  const handleToggleArmed = async (enabled: boolean) => {
    try {
      await putState(enabled).unwrap();
    } catch {
      raiseToast.error('Failed to update Mad Robot armed state.');
    }
  };

  const handleSaveRules = async () => {
    try {
      await putRules(localRules).unwrap();
      raiseToast.success('Mad Robot rules saved.');
    } catch {
      raiseToast.error('Failed to save Mad Robot rules.');
    }
  };

  const handleRowToggle = (id: string, enabled: boolean) => {
    setLocalRules(rs => rs.map(r => (r.id === id ? { ...r, enabled } : r)));
  };

  const handleDelete = (id: string) => {
    setLocalRules(rs => rs.filter(r => r.id !== id));
  };

  const handleLoadPreset = (key: string) => {
    const preset = MAD_ROBOT_PRESETS.find(p => p.key === key);
    if (!preset) return;
    // Prepend so the loaded preset is visible and takes first-match priority.
    setLocalRules(rs => [{ ...preset.rule, id: tempId() }, ...rs]);
    raiseToast.success(`Loaded preset: ${preset.label}`);
  };

  const handleReorder = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex === newIndex) return;
    setLocalRules(rs => {
      const next = [...rs];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      return next;
    });
  };

  const handleExport = () => {
    if (localRules.length === 0) {
      raiseToast.warning('No rules to export.');
      return;
    }
    downloadRulesFile(localRules);
  };

  const handleImportFile = async (file: File) => {
    try {
      const text = await file.text();
      const json: unknown = JSON.parse(text);
      const parsed = rulesPayloadSchema.safeParse(json);
      if (!parsed.success) {
        raiseToast.error(
          `Import failed: ${parsed.error.issues[0]?.message ?? 'invalid file'}`,
        );
        return;
      }
      // Fresh temp ids so React keys stay unique while editing; the server
      // swaps them for real UUIDs on save.
      const imported = parsed.data.rules.map(r => ({ ...r, id: tempId() }));
      setLocalRules(rs => [...rs, ...imported]);
      raiseToast.success(`Imported ${imported.length} rule(s).`);
    } catch (err) {
      raiseToast.error(
        `Import failed: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
  };

  const handleApplyDraft = () => {
    if (!draft) return;
    const trimmed: Rule = { ...draft, name: draft.name.trim() || 'Unnamed' };
    setLocalRules(rs => {
      const idx = rs.findIndex(r => r.id === trimmed.id);
      if (idx >= 0) {
        const next = [...rs];
        next[idx] = trimmed;
        return next;
      }
      // New rule goes to the top — visible immediately and first in the
      // match order. Its client temp id becomes a UUID when the ruleset saves.
      return [trimmed, ...rs];
    });
    setDraft(null);
  };

  const isLoading = isLoadingState || isLoadingRules;
  const isEditing = draft !== null;
  const isArmed = !!serverState?.enabled;
  // Count enabled rules from the server-saved set, not the local table: the
  // summary reports what's actually injecting, not pending unsaved edits.
  const activeCount = (serverRules ?? []).filter(rule => rule.enabled).length;

  const enabledSummary =
    activeCount === 0
      ? 'No rules are enabled'
      : `${activeCount} rule${activeCount === 1 ? '' : 's'} ${
          activeCount === 1 ? 'is' : 'are'
        } enabled`;

  // Describe how many rules are enabled, then prompt the toggle to its right;
  // the trailing colon points at the toggle.
  const statusText = isArmed
    ? `${enabledSummary}${
        activeCount === 0
          ? ', so nothing is being injected'
          : ' and injecting failures'
      }. Click the toggle to disable error injection:`
    : `${enabledSummary}. Click the toggle to start injecting failures:`;

  const subtitle = isEditing
    ? draft.id
      ? `Editing rule: ${draft.name || 'Unnamed'}`
      : 'New rule'
    : 'Inject failures into /service/* requests (dev/QA only)';

  return (
    <Modal
      className={s.root}
      id="madrobot"
      onAfterClose={() => {
        setDraft(null);
        setLocalRules(serverRules ?? []);
      }}
    >
      <Modal.Header
        icon={
          <img
            alt=""
            className={s.robotIcon}
            height={149}
            src="/img/robot_mad.svg"
            width={152}
          />
        }
        title="Mad Robot Chaos Injection"
        subtitle={subtitle}
      />

      <Modal.Content>
        {isLoading ? (
          <Loader size="large" active inline="centered">
            Loading…
          </Loader>
        ) : isEditing ? (
          <RuleEditor draft={draft} onChange={setDraft} />
        ) : (
          <>
            <div className={s.armedRow}>
              <div className={s.armedLabel}>
                <strong>
                  Chaos is{' '}
                  <Label
                    className={s.statusLabel}
                    color={isArmed ? 'red' : 'green'}
                    size="small"
                  >
                    {isArmed ? 'Active' : 'Inactive'}
                  </Label>
                </strong>
                <span>{statusText}</span>
              </div>
              <Toggle
                aria-label="Arm Mad Robot"
                checked={isArmed}
                disabled={isSavingState}
                onChange={(_, { checked }) => handleToggleArmed(!!checked)}
              />
            </div>

            <div className={s.rulesHeader}>
              <div className={s.toolbarGroup}>
                <Popup
                  basic
                  flowing
                  offset={[0, -8]}
                  on="click"
                  onClose={() => setIsPresetMenuOpen(false)}
                  onOpen={() => setIsPresetMenuOpen(true)}
                  open={isPresetMenuOpen}
                  position="bottom left"
                  trigger={
                    <button
                      aria-label="Load preset"
                      className={`ui button ${s.presetTrigger}`}
                      type="button"
                    >
                      <span className={s.presetTriggerLabel}>Load preset…</span>
                      <ChevronDown className={s.presetChevron} size={14} />
                    </button>
                  }
                >
                  <div className={s.presetList}>
                    {MAD_ROBOT_PRESETS.map(p => (
                      <button
                        className={s.presetItem}
                        key={p.key}
                        onClick={() => {
                          handleLoadPreset(p.key);
                          setIsPresetMenuOpen(false);
                        }}
                        type="button"
                      >
                        <div className={s.presetTitle}>{p.label}</div>
                        <div className={s.presetDescription}>
                          {p.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </Popup>
                <Button
                  content="Add rule"
                  icon={<Plus />}
                  onClick={() => setDraft(blankDraft())}
                />
              </div>
              <div className={s.toolbarGroup}>
                <Button
                  content="Import"
                  icon={<Upload />}
                  onClick={() => fileInputRef.current?.click()}
                />
                <Button
                  content="Export"
                  icon={<Download />}
                  onClick={handleExport}
                />
                <input
                  accept="application/json,.json"
                  className={s.hiddenInput}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    // handleImportFile handles its own errors; the no-op catch
                    // just satisfies no-floating-promises.
                    if (file) handleImportFile(file).catch(() => {});
                    // Clear the value so selecting the same file twice still fires.
                    e.target.value = '';
                  }}
                  ref={fileInputRef}
                  type="file"
                />
              </div>
            </div>

            {localRules.length === 0 ? (
              <div className={s.empty}>
                No rules yet. Add one to start injecting failures.
              </div>
            ) : (
              <div className={s.tableScroll}>
                <Table compact selectable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell collapsing aria-label="Reorder" />
                      <Table.HeaderCell>On</Table.HeaderCell>
                      <Table.HeaderCell>Name</Table.HeaderCell>
                      <Table.HeaderCell>Method</Table.HeaderCell>
                      <Table.HeaderCell>Path pattern</Table.HeaderCell>
                      <Table.HeaderCell>Mode</Table.HeaderCell>
                      <Table.HeaderCell>Probability</Table.HeaderCell>
                      <Table.HeaderCell collapsing />
                    </Table.Row>
                  </Table.Header>
                  <SortableRulesBody
                    helperClass={s.dragHelper}
                    lockAxis="y"
                    armed={isArmed}
                    savedSignatures={savedSignatures}
                    serverEnabledIds={serverEnabledIds}
                    onSortEnd={handleReorder}
                    rules={localRules}
                    onToggle={handleRowToggle}
                    onEdit={r => setDraft({ ...r })}
                    onDelete={handleDelete}
                    useDragHandle
                  />
                </Table>
              </div>
            )}
          </>
        )}
      </Modal.Content>

      <Modal.Actions className={s.footer}>
        {isEditing ? (
          <>
            <CancelButton content="Cancel" onClick={() => setDraft(null)} />
            <Button content="Apply" onClick={handleApplyDraft} primary />
          </>
        ) : (
          <>
            {isDirty && (
              <span className={s.dirtyHint}>
                There are unsaved changes, click <strong>Save</strong> to push
                rules to the server.
              </span>
            )}
            <CancelButton content="Close" onClick={close} />
            <Button
              content="Save"
              disabled={!isDirty || isSavingRules}
              loading={isSavingRules}
              onClick={handleSaveRules}
              primary
            />
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default MadRobotModal;
